import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const fetchImageBytesWithFallback = async (imageUrl) => {
    try {
        let response = await fetch(imageUrl);
        if (response.ok) {
            return await response.arrayBuffer();
        }
        
        // If Cloudinary URL failed, try Pollinations.ai
        if (imageUrl.includes("cloudinary.com")) {
            console.warn("Cloudinary fetch failed, falling back to Pollinations.ai...");
            const decoded = imageUrl.match(/e_gen_text_to_image:prompt_(.*?)\//)?.[1] || "book cover";
            const fallbackUrl = `https://image.pollinations.ai/prompt/${decoded}?width=400&height=600&nologo=true`;
            response = await fetch(fallbackUrl);
            if (response.ok) {
                return await response.arrayBuffer();
            }
        }
    } catch (err) {
        console.error("Initial cover image fetch failed, attempting ultimate fallback...", err);
    }

    // Ultimate fallback: Fetch a beautiful placeholder image from Picsum (which has full CORS support)
    console.warn("Falling back to Picsum Photos for cover image...");
    try {
        const picsumUrl = `https://picsum.photos/400/600`;
        const response = await fetch(picsumUrl);
        if (response.ok) {
            return await response.arrayBuffer();
        }
    } catch (err) {
        console.error("Failed to fetch Picsum fallback image:", err);
    }

    throw new Error("Failed to load any cover image or fallback image.");
};

const cleanTextForWinAnsi = (text) => {
    if (!text) return "";
    return text
        .replace(/\t/g, "    ")          // Replace tab characters with 4 spaces
        .replace(/[\u2018\u2019]/g, "'") // Smart single quotes / apostrophes
        .replace(/[\u201c\u201d]/g, '"') // Smart double quotes
        .replace(/[\u2014]/g, "--")      // Em dash
        .replace(/[\u2013]/g, "-")       // En dash
        .replace(/[\u2026]/g, "...")     // Ellipsis
        // eslint-disable-next-line no-control-regex
        .replace(/[^\x0A\x20-\x7F]/g, "") // Strip non-printable/control chars and non-ASCII
        .trim();
};

const drawCopyrightFooter = (page, author, font) => {
    const yLine = 75;
    // Draw separator line
    page.drawLine({
        start: { x: 50, y: yLine },
        end: { x: 550, y: yLine },
        thickness: 0.5,
        color: rgb(0.7, 0.7, 0.7),
    });
    
    // Draw copyright text
    const copyrightText = `© ${new Date().getFullYear()} ${author}. All rights reserved. | Published via Book Creation Platform`;
    page.drawText(copyrightText, {
        x: 50,
        y: 55,
        size: 9,
        font: font,
        color: rgb(0.5, 0.5, 0.5),
    });
};

// Vector barcode generator for the PDF back cover
const drawMockBarcode = (page, x, y, width, height) => {
    // Draw white background
    page.drawRectangle({
        x: x,
        y: y,
        width: width,
        height: height,
        color: rgb(1, 1, 1),
    });
    
    // Draw vertical barcode lines of random thicknesses
    let currentX = x + 5;
    const endX = x + width - 5;
    while (currentX < endX) {
        const thickness = Math.random() > 0.5 ? 2.0 : 0.8;
        const spacing = Math.floor(Math.random() * 3) + 1;
        if (currentX + thickness <= endX) {
            page.drawLine({
                start: { x: currentX, y: y + 8 },
                end: { x: currentX, y: y + height - 12 },
                thickness: thickness,
                color: rgb(0, 0, 0),
            });
        }
        currentX += thickness + spacing;
    }

    // Draw text "ISBN 978-3-16-148410-0" under the lines
    page.drawText("ISBN 978-3-16-148410-0", {
        x: x + 6,
        y: y + 2,
        size: 5,
        color: rgb(0, 0, 0),
    });
};

const cleanManuscriptText = (text) => {
    if (!text) return "";
    return text;
};

const isChapterHeading = (line) => {
    return false;
};

// HTML parser that returns paragraphs of styled text runs
const parseHtmlToStyledParagraphs = (html) => {
    if (!html) return [];
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const paragraphs = [];
    
    const blockElements = Array.from(doc.body.childNodes);
    
    for (const el of blockElements) {
        if (el.nodeType === 3) { // Node.TEXT_NODE
            const text = cleanTextForWinAnsi(el.textContent);
            if (text) {
                paragraphs.push([{ text, bold: false, italic: false, underline: false }]);
            }
        } else if (el.nodeType === 1) { // Node.ELEMENT_NODE
            const tagName = el.tagName.toLowerCase();
            
            if (/^h[1-6]$/.test(tagName)) {
                const headingLevel = parseInt(tagName.substring(1));
                const segments = extractInlineSegments(el);
                segments.forEach(seg => {
                    seg.isHeading = true;
                    seg.headingLevel = headingLevel;
                });
                paragraphs.push(segments);
            } else if (tagName === "p" || tagName === "div" || tagName === "li") {
                const segments = extractInlineSegments(el);
                if (segments.length > 0) {
                    paragraphs.push(segments);
                }
            } else if (tagName === "br") {
                paragraphs.push([{ text: "\n", bold: false, italic: false, underline: false }]);
            } else {
                const segments = extractInlineSegments(el);
                if (segments.length > 0) {
                    paragraphs.push(segments);
                }
            }
        }
    }
    
    return paragraphs;
};

const extractInlineSegments = (element) => {
    const segments = [];
    
    const walk = (node, isBold, isItalic, isUnderline, currentColor) => {
        if (node.nodeType === 3) { // TEXT
            const text = cleanTextForWinAnsi(node.textContent);
            if (text) {
                segments.push({
                    text,
                    bold: isBold,
                    italic: isItalic,
                    underline: isUnderline,
                    color: currentColor
                });
            }
        } else if (node.nodeType === 1) { // ELEMENT
            const tag = node.tagName.toLowerCase();
            let b = isBold;
            let i = isItalic;
            let u = isUnderline;
            let c = currentColor;
            
            if (tag === "strong" || tag === "b") b = true;
            if (tag === "em" || tag === "i") i = true;
            if (tag === "u") u = true;
            
            // Check for font color attribute
            if (tag === "font") {
                const colorAttr = node.getAttribute("color");
                if (colorAttr) {
                    c = colorAttr;
                }
            }
            // Check for inline span style color
            if (tag === "span") {
                const styleAttr = node.getAttribute("style");
                if (styleAttr) {
                    const colorMatch = styleAttr.match(/color:\s*([^;]+)/i);
                    if (colorMatch) {
                        c = colorMatch[1].trim();
                    }
                }
            }
            
            if (tag === "br") {
                segments.push({ text: "\n", bold: false, italic: false, underline: false, color: null });
            }
            
            for (const child of node.childNodes) {
                walk(child, b, i, u, c);
            }
        }
    };
    
    for (const child of element.childNodes) {
        walk(child, false, false, false, null);
    }
    
    return segments;
};

const parseStringToRgb = (colorStr, defaultColor = rgb(0, 0, 0)) => {
    if (!colorStr) return defaultColor;
    const s = colorStr.trim().toLowerCase();
    
    // Check for rgb() / rgba()
    if (s.startsWith("rgb")) {
        const matches = s.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (matches) {
            const r = parseInt(matches[1], 10) / 255;
            const g = parseInt(matches[2], 10) / 255;
            const b = parseInt(matches[3], 10) / 255;
            return rgb(
                isNaN(r) ? defaultColor.red : r,
                isNaN(g) ? defaultColor.green : g,
                isNaN(b) ? defaultColor.blue : b
            );
        }
    }
    
    // Check for hex or starts with #
    if (s.startsWith("#") || /^[0-9a-f]{3,6}$/i.test(s)) {
        let hex = s.replace("#", "");
        if (hex.length === 3) {
            hex = hex.split("").map(char => char + char).join("");
        }
        const r = parseInt(hex.substring(0, 2), 16) / 255;
        const g = parseInt(hex.substring(2, 4), 16) / 255;
        const b = parseInt(hex.substring(4, 6), 16) / 255;
        return rgb(
            isNaN(r) ? defaultColor.red : r,
            isNaN(g) ? defaultColor.green : g,
            isNaN(b) ? defaultColor.blue : b
        );
    }
    
    // Named colors map
    const namedColors = {
        black: rgb(0, 0, 0),
        white: rgb(1, 1, 1),
        red: rgb(1, 0, 0),
        green: rgb(0, 1, 0),
        blue: rgb(0, 0, 1),
        yellow: rgb(1, 1, 0),
        magenta: rgb(1, 0, 1),
        cyan: rgb(0, 1, 1),
        gray: rgb(0.5, 0.5, 0.5),
        grey: rgb(0.5, 0.5, 0.5),
        lightgray: rgb(0.83, 0.83, 0.83),
        darkgray: rgb(0.66, 0.66, 0.66),
        orange: rgb(1, 0.65, 0),
        purple: rgb(0.5, 0, 0.5),
        pink: rgb(1, 0.75, 0.8),
        brown: rgb(0.6, 0.4, 0.2),
        indigo: rgb(0.29, 0, 0.51),
        violet: rgb(0.93, 0.51, 0.93)
    };
    
    if (namedColors[s]) {
        return namedColors[s];
    }
    
    return defaultColor;
};

const getFontForStyle = (fonts, bold, italic) => {
    if (bold && italic) return fonts.boldItalic;
    if (bold) return fonts.bold;
    if (italic) return fonts.italic;
    return fonts.regular;
};

const layoutRichTextBlock = (
    htmlContent,
    {
        maxWidth,
        defaultSize,
        casing = "normal",
        blockFontWeight = "normal",
        blockFontStyle = "normal",
        activeFonts,
        lineHeightMultiplier = 1.25
    }
) => {
    const paragraphs = parseHtmlToStyledParagraphs(htmlContent);
    const layoutLines = [];
    let totalHeight = 0;
    const lineSpacing = defaultSize * lineHeightMultiplier;
    const paragraphSpacing = defaultSize * 0.4;

    for (let pIdx = 0; pIdx < paragraphs.length; pIdx++) {
        const paragraph = paragraphs[pIdx];
        
        const processedParagraph = paragraph.map(seg => {
            let processedText = cleanTextForWinAnsi(seg.text);
            if (casing === "uppercase") {
                processedText = processedText.toUpperCase();
            } else if (casing === "lowercase") {
                processedText = processedText.toLowerCase();
            } else if (casing === "capitalize") {
                processedText = processedText.replace(/\b\w/g, char => char.toUpperCase());
            }
            
            const isBold = seg.bold || (blockFontWeight === "bold");
            const isItalic = seg.italic || (blockFontStyle === "italic");
            const font = getFontForStyle(activeFonts, isBold, isItalic);
            
            return {
                ...seg,
                text: processedText,
                bold: isBold,
                italic: isItalic,
                font,
                fontSize: defaultSize
            };
        });

        const pLines = [];
        let currentLine = [];
        let currentLineWidth = 0;

        for (const seg of processedParagraph) {
            const tokens = seg.text.split(/(\s+)/);
            for (const token of tokens) {
                if (token === "\n") {
                    pLines.push(currentLine);
                    currentLine = [];
                    currentLineWidth = 0;
                    continue;
                }
                if (!token) continue;

                const tokenWidth = seg.font.widthOfTextAtSize(token, seg.fontSize);

                if (currentLineWidth + tokenWidth > maxWidth) {
                    if (currentLine.length === 0 && /^\s+$/.test(token)) {
                        continue;
                    }
                    pLines.push(currentLine);
                    currentLine = [{
                        ...seg,
                        text: token,
                        width: tokenWidth
                    }];
                    currentLineWidth = tokenWidth;
                } else {
                    currentLine.push({
                        ...seg,
                        text: token,
                        width: tokenWidth
                    });
                    currentLineWidth += tokenWidth;
                }
            }
        }
        if (currentLine.length > 0) {
            pLines.push(currentLine);
        }

        if (pLines.length > 0) {
            layoutLines.push(...pLines);
            totalHeight += pLines.length * lineSpacing;
            if (pIdx < paragraphs.length - 1) {
                totalHeight += paragraphSpacing;
            }
        }
    }

    return {
        lines: layoutLines,
        totalHeight,
        lineSpacing,
        paragraphSpacing
    };
};

const drawLayoutLines = (
    page,
    layout,
    {
        x,
        yStart,
        maxWidth,
        defaultColor,
        alignment = "left",
        hasShadow = false
    }
) => {
    let currentY = yStart;
    
    for (let i = 0; i < layout.lines.length; i++) {
        const line = layout.lines[i];
        const lineWidth = line.reduce((sum, seg) => sum + seg.width, 0);

        let lineStartX = x;
        if (alignment === "center") {
            lineStartX = x + (maxWidth - lineWidth) / 2;
        } else if (alignment === "right") {
            lineStartX = x + (maxWidth - lineWidth);
        }

        if (hasShadow) {
            let segmentX = lineStartX + 1.5;
            for (const seg of line) {
                page.drawText(seg.text, {
                    x: segmentX,
                    y: currentY - 2,
                    size: seg.fontSize,
                    font: seg.font,
                    color: rgb(0, 0, 0)
                });
                segmentX += seg.width;
            }
        }

        let segmentX = lineStartX;
        for (const seg of line) {
            const segColor = seg.color ? parseStringToRgb(seg.color, defaultColor) : defaultColor;
            
            page.drawText(seg.text, {
                x: segmentX,
                y: currentY,
                size: seg.fontSize,
                font: seg.font,
                color: segColor
            });
            segmentX += seg.width;
        }

        currentY -= layout.lineSpacing;
    }
    
    return currentY;
};

// Wraps a paragraph of segments to lines that fit inside contentWidth
const wrapParagraph = (paragraph, contentWidth, defaultSize, activePageFonts) => {
    const lines = [];
    let currentLine = [];
    let currentLineWidth = 0;
    
    const getFontForSegment = (seg) => {
        if (seg.bold && seg.italic) {
            return activePageFonts.boldItalic;
        } else if (seg.bold) {
            return activePageFonts.bold;
        } else if (seg.italic) {
            return activePageFonts.italic;
        }
        return activePageFonts.regular;
    };
    
    const getSizeForSegment = (seg) => {
        if (seg.isHeading) {
            if (seg.headingLevel === 1) return defaultSize + 8;
            if (seg.headingLevel === 2) return defaultSize + 6;
            return defaultSize + 4;
        }
        return defaultSize;
    };
    
    for (const seg of paragraph) {
        const words = seg.text.split(/(\s+)/);
        const font = getFontForSegment(seg);
        const fontSize = getSizeForSegment(seg);
        
        for (const word of words) {
            if (word === "\n") {
                lines.push(currentLine);
                currentLine = [];
                currentLineWidth = 0;
                continue;
            }
            if (!word) continue;
            
            const wordWidth = font.widthOfTextAtSize(word, fontSize);
            
            if (currentLineWidth + wordWidth > contentWidth) {
                if (currentLine.length === 0 && /^\s+$/.test(word)) {
                    continue;
                }
                lines.push(currentLine);
                currentLine = [{
                    text: word,
                    bold: seg.bold,
                    italic: seg.italic,
                    underline: seg.underline,
                    isHeading: seg.isHeading,
                    headingLevel: seg.headingLevel,
                    font,
                    fontSize,
                    width: wordWidth
                }];
                currentLineWidth = wordWidth;
            } else {
                currentLine.push({
                    text: word,
                    bold: seg.bold,
                    italic: seg.italic,
                    underline: seg.underline,
                    isHeading: seg.isHeading,
                    headingLevel: seg.headingLevel,
                    font,
                    fontSize,
                    width: wordWidth
                });
                currentLineWidth += wordWidth;
            }
        }
    }
    
    if (currentLine.length > 0) {
        lines.push(currentLine);
    }
    
    return lines;
};

export const getPageDimensions = (size) => {
    switch (size) {
        case "pocket_book": return { width: 306, height: 495 };
        case "pocket":
        case "small_paperback":
            return { width: 360, height: 576 };
        case "digest":
        case "digest_size":
            return { width: 396, height: 612 };
        case "trade":
        case "trade_paperback":
            return { width: 432, height: 648 };
        case "large_book": return { width: 504, height: 720 };
        case "childrens_book": return { width: 576, height: 720 };
        case "square_book": return { width: 612, height: 612 };
        case "letter":
        case "textbook":
            return { width: 612, height: 792 };
        case "a5": return { width: 420, height: 595 };
        case "a4": return { width: 595, height: 842 };
        default:
            return { width: 432, height: 648 };
    }
};

export const generateBookPdf = async ({
    title,
    author,
    content,
    description,
    frontCover,
    backCover,
    aboutAuthor,
    authorPhoto,
    coverMode = "image",
    coverBgColor = "#DC2626",
    coverTextColor = "#FFFFFF",
    coverFont = "serif",
    titleColor = "#FFFFFF",
    titleSize = 28,
    titleTop = 35,
    titleLeft = 10,
    titleWidth = 60,
    titleFontWeight = "bold",
    titleFontStyle = "normal",
    authorColor = "#FFFFFF",
    authorSize = 16,
    authorTop = 75,
    authorLeft = 10,
    authorWidth = 60,
    authorFontWeight = "bold",
    authorFontStyle = "normal",
    descColor = "#FFFFFF",
    descSize = 13,
    descTop = 15,
    descLeft = 8,
    descWidth = 60,
    descFontWeight = "normal",
    descFontStyle = "italic",
    profileColor = "#FFFFFF",
    profileSize = 10,
    profileTop = 65,
    profileLeft = 8,
    profileWidth = 60,
    profileFontWeight = "normal",
    profileFontStyle = "italic",
    authorSignature,
    pageSize,
    margins,
    theme,
    headerFooterEnabled,
    pageBorder = "none",
    
    // Cover Text, Casing & Alignment Overrides
    coverTitle,
    coverAuthor,
    coverDescription,
    coverProfileText,
    titleAlign = "center",
    authorAlign = "center",
    descAlign = "left",
    profileAlign = "left",
    titleCase = "uppercase",
    authorCase = "uppercase",
    descCase = "normal",
    profileCase = "normal",
}) => {
    const hexToRgbColor = (hex, defaultColor = rgb(0.86, 0.15, 0.15)) => {
        if (!hex) return defaultColor;
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
            cleanHex = cleanHex.split('').map(char => char + char).join('');
        }
        const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
        const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
        const b = parseInt(cleanHex.substring(4, 6), 16) / 255;
        return rgb(isNaN(r) ? defaultColor.red : r, isNaN(g) ? defaultColor.green : g, isNaN(b) ? defaultColor.blue : b);
    };

    // Text casing helper function
    const applyTextCase = (text, caseType) => {
        if (!text) return "";
        if (caseType === "uppercase") {
            return text.toUpperCase();
        } else if (caseType === "lowercase") {
            return text.toLowerCase();
        } else if (caseType === "capitalize") {
            return text.replace(/\b\w/g, char => char.toUpperCase());
        }
        return text;
    };

    // Sanitize parameters for WinAnsi compatibility to prevent PDF encoder crashes
    title = cleanTextForWinAnsi(title);
    author = cleanTextForWinAnsi(author);
    content = cleanTextForWinAnsi(content);
    description = cleanTextForWinAnsi(description);
    aboutAuthor = cleanTextForWinAnsi(aboutAuthor);
    
    // Cover page content blocks with correct defaults/overrides
    const activeCoverTitle = coverTitle !== undefined && coverTitle !== "" ? coverTitle : (title || "Untitled");
    const activeCoverAuthor = coverAuthor !== undefined && coverAuthor !== "" ? coverAuthor : (author || "Author");
    const activeCoverDescription = coverDescription !== undefined && coverDescription !== "" ? coverDescription : (aboutAuthor || `${activeCoverAuthor} is the visionary creator of this book.`);
    const activeCoverProfileText = coverProfileText !== undefined && coverProfileText !== "" ? coverProfileText : (description || "A fascinating journey through character and destiny.");

    let activePageSize = pageSize || "trade";
    const coverDims = getPageDimensions(activePageSize);
    const coverWidth = coverDims.width;
    const coverHeight = coverDims.height;

    const pdfDoc = await PDFDocument.create();
    
    // Embed fonts
    const timesRoman = await pdfDoc.embedFont(StandardFonts.TimesRoman);
    const timesRomanBold = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
    const timesItalic = await pdfDoc.embedFont(StandardFonts.TimesRomanItalic);
    const timesBoldItalic = await pdfDoc.embedFont(StandardFonts.TimesRomanBoldItalic);

    const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const helveticaOblique = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
    const helveticaBoldOblique = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
    
    const courier = await pdfDoc.embedFont(StandardFonts.Courier);
    const courierBold = await pdfDoc.embedFont(StandardFonts.CourierBold);
    const courierOblique = await pdfDoc.embedFont(StandardFonts.CourierOblique);
    const courierBoldOblique = await pdfDoc.embedFont(StandardFonts.CourierBoldOblique);

    const getCoverFonts = (f) => {
        if (f === "sans-serif") {
            return {
                regular: helvetica,
                bold: helveticaBold,
                italic: helveticaOblique,
                boldItalic: helveticaBoldOblique
            };
        } else if (f === "monospace") {
            return {
                regular: courier,
                bold: courierBold,
                italic: courierOblique,
                boldItalic: courierBoldOblique
            };
        }
        return {
            regular: timesRoman,
            bold: timesRomanBold,
            italic: timesItalic,
            boldItalic: timesBoldItalic
        };
    };
    
    const activeFonts = getCoverFonts(coverFont);

    // 1. Draw Front Cover
    const frontCoverPage = pdfDoc.addPage([coverWidth, coverHeight]);
    let drawFrontImageSuccess = false;
    
    if (coverMode === "image" && frontCover) {
        try {
            const imgBytes = await fetchImageBytesWithFallback(frontCover);
            let img;
            try {
                img = await pdfDoc.embedJpg(imgBytes);
            } catch {
                img = await pdfDoc.embedPng(imgBytes);
            }
            
            frontCoverPage.drawImage(img, {
                x: 0,
                y: 0,
                width: coverWidth,
                height: coverHeight,
            });
            drawFrontImageSuccess = true;
        } catch (error) {
            console.error("Error embedding front cover image, falling back to solid color:", error);
        }
    }
    
    if (!drawFrontImageSuccess) {
        frontCoverPage.drawRectangle({
            x: 0,
            y: 0,
            width: coverWidth,
            height: coverHeight,
            color: hexToRgbColor(coverBgColor, rgb(0.86, 0.15, 0.15)),
        });
    }

    // Draw Front Cover overlays (Title & Author using coordinates and text colors)
    const finalTitleWidth = (titleWidth / 100) * coverWidth;
    const titleLayout = layoutRichTextBlock(
        activeCoverTitle,
        {
            maxWidth: finalTitleWidth,
            defaultSize: titleSize,
            casing: titleCase,
            blockFontWeight: titleFontWeight,
            blockFontStyle: titleFontStyle,
            activeFonts
        }
    );
    const titleStartTopPx = (titleTop / 100) * coverHeight;
    const titleY = coverHeight - titleStartTopPx - titleSize;
    const titleRgbColor = hexToRgbColor(titleColor, rgb(1, 1, 1));
    const titleX = (titleLeft / 100) * coverWidth;

    drawLayoutLines(
        frontCoverPage,
        titleLayout,
        {
            x: titleX,
            yStart: titleY,
            maxWidth: finalTitleWidth,
            defaultColor: titleRgbColor,
            alignment: titleAlign,
            hasShadow: (coverMode === "image")
        }
    );

    const finalAuthorWidth = (authorWidth / 100) * coverWidth;
    const authorLayout = layoutRichTextBlock(
        activeCoverAuthor,
        {
            maxWidth: finalAuthorWidth,
            defaultSize: authorSize,
            casing: authorCase,
            blockFontWeight: authorFontWeight,
            blockFontStyle: authorFontStyle,
            activeFonts
        }
    );
    const authorStartTopPx = (authorTop / 100) * coverHeight;
    const authorY = coverHeight - authorStartTopPx - authorSize;
    const authorRgbColor = hexToRgbColor(authorColor, rgb(1, 1, 1));
    const authorX = (authorLeft / 100) * coverWidth;

    drawLayoutLines(
        frontCoverPage,
        authorLayout,
        {
            x: authorX,
            yStart: authorY,
            maxWidth: finalAuthorWidth,
            defaultColor: authorRgbColor,
            alignment: authorAlign,
            hasShadow: (coverMode === "image")
        }
    );
    let hasJsonPages = false;
    let manuscriptPages = [];
    activePageSize = pageSize || "trade";
    let activeMargins = margins || "normal";
    let activeTheme = theme || "cream";
    let activePageBorder = pageBorder || "none";

    // 2. Paginated Manuscript Content
    let activeHeaderFooterEnabled = headerFooterEnabled !== false;

    try {
        if (content && (content.trim().startsWith("{") || content.trim().startsWith("["))) {
            const parsed = JSON.parse(content);
            if (parsed) {
                if (Array.isArray(parsed)) {
                    manuscriptPages = parsed;
                    hasJsonPages = true;
                } else if (parsed.pages) {
                    manuscriptPages = parsed.pages;
                    hasJsonPages = true;
                    if (parsed.docSettings) {
                        const ds = parsed.docSettings;
                        activePageSize = ds.pageSize || activePageSize;
                        activeMargins = ds.margins || activeMargins;
                        activeTheme = ds.theme || activeTheme;
                        activeHeaderFooterEnabled = ds.headerFooterEnabled !== false;
                        activePageBorder = ds.pageBorder || activePageBorder;
                    }
                }
            }
        }
    } catch (err) {
        console.warn("Could not parse manuscriptContent as JSON, falling back to plain text pagination.");
    }

    // use module-level getPageDimensions

    const getMarginValues = (marginType, dims) => {
        let val = 54; // 0.75 in default
        if (marginType === "wide") val = 72; // 1.0 in
        else if (marginType === "narrow") val = 36; // 0.5 in
        
        return {
            left: val,
            right: val,
            top: val + 15,
            bottom: val + 15
        };
    };

    const getThemeColors = (themeName) => {
        switch (themeName) {
            case "white":
                return {
                    bg: rgb(1, 1, 1),
                    text: rgb(0.12, 0.12, 0.12)
                };
            case "sepia":
                return {
                    bg: rgb(0.95, 0.92, 0.84),
                    text: rgb(0.26, 0.20, 0.13)
                };
            case "charcoal":
                return {
                    bg: rgb(0.1, 0.1, 0.1),
                    text: rgb(0.95, 0.95, 0.95)
                };
            case "cream":
            default:
                return {
                    bg: rgb(0.98, 0.96, 0.93),
                    text: rgb(0.18, 0.15, 0.12)
                };
        }
    };

    if (hasJsonPages && manuscriptPages.length > 0) {
        // Draw pages page-by-page as structured in the json pages list
        for (let pageIdx = 0; pageIdx < manuscriptPages.length; pageIdx++) {
            const pageData = manuscriptPages[pageIdx];
            const dims = getPageDimensions(activePageSize);
            const m = getMarginValues(activeMargins, dims);
            const colors = getThemeColors(activeTheme);
            const contentWidth = dims.width - m.left - m.right;
            
            const pdfPage = pdfDoc.addPage([dims.width, dims.height]);
            
            // Draw background theme
            pdfPage.drawRectangle({
                x: 0,
                y: 0,
                width: dims.width,
                height: dims.height,
                color: colors.bg
            });

            // Draw page border if enabled
            if (activePageBorder && activePageBorder !== "none") {
                const borderOffset = 18;
                const borderX = borderOffset;
                const borderY = borderOffset;
                const borderWidth = dims.width - (borderOffset * 2);
                const borderHeight = dims.height - (borderOffset * 2);
                
                if (activePageBorder === "single") {
                    pdfPage.drawRectangle({
                        x: borderX,
                        y: borderY,
                        width: borderWidth,
                        height: borderHeight,
                        borderColor: colors.text,
                        borderWidth: 1,
                        color: colors.bg,
                        opacity: 0,
                    });
                } else if (activePageBorder === "double") {
                    // Outer line
                    pdfPage.drawRectangle({
                        x: borderX,
                        y: borderY,
                        width: borderWidth,
                        height: borderHeight,
                        borderColor: colors.text,
                        borderWidth: 1.2,
                        color: colors.bg,
                        opacity: 0,
                    });
                    // Inner line
                    pdfPage.drawRectangle({
                        x: borderX + 3,
                        y: borderY + 3,
                        width: borderWidth - 6,
                        height: borderHeight - 6,
                        borderColor: colors.text,
                        borderWidth: 0.5,
                        color: colors.bg,
                        opacity: 0,
                    });
                }
            }

            // Running headers and page numbers
            if (activeHeaderFooterEnabled && pageData.type !== "title" && pageData.type !== "blank") {
                const headerY = dims.height - m.top + 15;
                const headerText = cleanTextForWinAnsi(title || "Manuscript");
                
                pdfPage.drawText(headerText, {
                    x: m.left,
                    y: headerY,
                    size: 8,
                    font: timesRoman,
                    color: colors.text,
                    opacity: 0.4
                });

                // Footer centered page number
                const footerY = m.bottom - 20;
                const pageNumText = `— Page ${pageIdx + 1} —`;
                const pNumWidth = timesRoman.widthOfTextAtSize(pageNumText, 8);
                pdfPage.drawText(pageNumText, {
                    x: (dims.width - pNumWidth) / 2,
                    y: footerY,
                    size: 8,
                    font: timesRoman,
                    color: colors.text,
                    opacity: 0.4
                });
            }

            let textY = dims.height - m.top;

            // Handle Page Images
            const topImages = (pageData.images || []).filter(img => img.position === "top" || img.position === "full");
            const centerImages = (pageData.images || []).filter(img => img.position === "center" || (img.position === "full" && pageData.type === "image"));
            const bottomImages = (pageData.images || []).filter(img => img.position === "bottom");

            let bottomReservedHeight = 0;
            if (bottomImages.length > 0) {
                bottomReservedHeight = 150; // reserve space at bottom
            }

            // Draw Top Image
            for (const imgConfig of topImages) {
                try {
                    const imgBytes = await fetchImageBytesWithFallback(imgConfig.url);
                    let img;
                    try {
                        img = await pdfDoc.embedJpg(imgBytes);
                    } catch {
                        img = await pdfDoc.embedPng(imgBytes);
                    }
                    
                    if (img) {
                        const imgHeight = 130;
                        const imgWidth = Math.min(contentWidth, (img.width / img.height) * imgHeight);
                        const imgX = m.left + (contentWidth - imgWidth) / 2;
                        
                        pdfPage.drawImage(img, {
                            x: imgX,
                            y: textY - imgHeight,
                            width: imgWidth,
                            height: imgHeight
                        });

                        textY -= (imgHeight + 10);
                        
                        if (imgConfig.caption) {
                            const cleanCap = cleanTextForWinAnsi(imgConfig.caption);
                            const capWidth = timesItalic.widthOfTextAtSize(cleanCap, 7);
                            pdfPage.drawText(cleanCap, {
                                x: (dims.width - capWidth) / 2,
                                y: textY,
                                size: 7,
                                font: timesItalic,
                                color: colors.text,
                                opacity: 0.7
                            });
                            textY -= 12;
                        }
                        textY -= 8;
                    }
                } catch (err) {
                    console.error("Error drawing top image on pdf page:", err);
                }
            }

            // Draw Title layout
            if (pageData.type === "title") {
                textY = dims.height * 0.65;
                const cleanBookTitle = cleanTextForWinAnsi(title.toUpperCase());
                const tWidth = timesRomanBold.widthOfTextAtSize(cleanBookTitle, 22);
                pdfPage.drawText(cleanBookTitle, {
                    x: (dims.width - tWidth) / 2,
                    y: textY,
                    size: 22,
                    font: timesRomanBold,
                    color: colors.text
                });

                textY -= 35;
                const cleanAuth = cleanTextForWinAnsi(`BY ${author.toUpperCase()}`);
                const aWidth = timesRoman.widthOfTextAtSize(cleanAuth, 12);
                pdfPage.drawText(cleanAuth, {
                    x: (dims.width - aWidth) / 2,
                    y: textY,
                    size: 12,
                    font: timesRoman,
                    color: colors.text
                });
                
                textY -= 50;
            }

            // Draw Blank Page layout
            if (pageData.type === "blank") {
                continue;
            }

            // Draw Image Page layout
            if (pageData.type === "image" || centerImages.length > 0) {
                let imgHeight = dims.height * 0.55;
                let imgY = (dims.height - imgHeight) / 2;

                const targetImages = centerImages.length > 0 ? centerImages : (pageData.images || []);
                for (const imgConfig of targetImages) {
                    try {
                        const imgBytes = await fetchImageBytesWithFallback(imgConfig.url);
                        let img;
                        try {
                            img = await pdfDoc.embedJpg(imgBytes);
                        } catch {
                            img = await pdfDoc.embedPng(imgBytes);
                        }
                        
                        if (img) {
                            imgHeight = dims.height * 0.55;
                            const imgWidth = Math.min(contentWidth, (img.width / img.height) * imgHeight);
                            const imgX = m.left + (contentWidth - imgWidth) / 2;
                            imgY = (dims.height - imgHeight) / 2;
                            
                            pdfPage.drawImage(img, {
                                x: imgX,
                                y: imgY,
                                width: imgWidth,
                                height: imgHeight
                            });

                            if (imgConfig.caption) {
                                const cleanCap = cleanTextForWinAnsi(imgConfig.caption);
                                const capWidth = timesItalic.widthOfTextAtSize(cleanCap, 8);
                                pdfPage.drawText(cleanCap, {
                                    x: (dims.width - capWidth) / 2,
                                    y: imgY - 18,
                                    size: 8,
                                    font: timesItalic,
                                    color: colors.text,
                                    opacity: 0.8
                                });
                            }
                        }
                    } catch (err) {
                        console.error("Error drawing image page:", err);
                    }
                }
                textY = imgY - 35;
            }

            // Parse text to styled paragraphs
            const fontName = pageData.fontFamily || "serif";
            const fontSize = pageData.fontSize || 11;
            const alignment = pageData.alignment || "justify";
            
            const activePageFonts = getCoverFonts(fontName);
            const paragraphs = parseHtmlToStyledParagraphs(pageData.content);
            const bodyLineHeight = fontSize * 1.45;

            // Draw paragraphs
            for (const p of paragraphs) {
                const wrappedLines = wrapParagraph(p, contentWidth, fontSize, activePageFonts);
                
                for (let lineIdx = 0; lineIdx < wrappedLines.length; lineIdx++) {
                    const line = wrappedLines[lineIdx];
                    if (textY < m.bottom + bottomReservedHeight) {
                        break;
                    }

                    // Calculate total line width
                    let lineTotalWidth = 0;
                    let spaceCharCount = 0;
                    for (const tok of line) {
                        lineTotalWidth += tok.width;
                        if (tok.text === " " || tok.text.trim() === "") {
                            spaceCharCount++;
                        }
                    }

                    let justifySpacing = 0;
                    const isLastLineOfParagraph = (lineIdx === wrappedLines.length - 1);
                    if (alignment === "justify" && !isLastLineOfParagraph && spaceCharCount > 0) {
                        const remWidth = contentWidth - lineTotalWidth;
                        if (remWidth > 0 && remWidth < 60) {
                            justifySpacing = remWidth / spaceCharCount;
                        }
                    }

                    let startX = m.left;
                    if (alignment === "center") {
                        startX = m.left + (contentWidth - lineTotalWidth) / 2;
                    } else if (alignment === "right") {
                        startX = m.left + contentWidth - lineTotalWidth;
                    }

                    // Draw tokens
                    let tokenX = startX;
                    for (const tok of line) {
                        const cleanTok = cleanTextForWinAnsi(tok.text);
                        
                        pdfPage.drawText(cleanTok, {
                            x: tokenX,
                            y: textY,
                            size: tok.fontSize,
                            font: tok.font,
                            color: colors.text
                        });

                        if (tok.underline) {
                            pdfPage.drawLine({
                                start: { x: tokenX, y: textY - 2 },
                                end: { x: tokenX + tok.width, y: textY - 2 },
                                thickness: 0.8,
                                color: colors.text
                            });
                        }

                        let advanceWidth = tok.width;
                        if (tok.text === " " || tok.text.trim() === "") {
                            advanceWidth += justifySpacing;
                        }
                        tokenX += advanceWidth;
                    }

                    textY -= bodyLineHeight;
                }
                
                textY -= 8; // spacing between paragraphs
                if (textY < m.bottom + bottomReservedHeight) {
                    break;
                }
            }

            // Draw Bottom Image
            for (const imgConfig of bottomImages) {
                try {
                    const imgBytes = await fetchImageBytesWithFallback(imgConfig.url);
                    let img;
                    try {
                        img = await pdfDoc.embedJpg(imgBytes);
                    } catch {
                        img = await pdfDoc.embedPng(imgBytes);
                    }
                    
                    if (img) {
                        const imgHeight = 110;
                        const imgWidth = Math.min(contentWidth, (img.width / img.height) * imgHeight);
                        const imgX = m.left + (contentWidth - imgWidth) / 2;
                        const imgY = m.bottom + 15;
                        
                        pdfPage.drawImage(img, {
                            x: imgX,
                            y: imgY,
                            width: imgWidth,
                            height: imgHeight
                        });

                        if (imgConfig.caption) {
                            const cleanCap = cleanTextForWinAnsi(imgConfig.caption);
                            const capWidth = timesItalic.widthOfTextAtSize(cleanCap, 7);
                            pdfPage.drawText(cleanCap, {
                                x: (dims.width - capWidth) / 2,
                                y: imgY - 10,
                                size: 7,
                                font: timesItalic,
                                color: colors.text,
                                opacity: 0.7
                            });
                        }
                    }
                } catch (err) {
                    console.error("Error drawing bottom image on pdf page:", err);
                }
            }
        }
    } else {
        // Fallback plain text renderer
        if (content) {
            const cleanedContent = cleanManuscriptText(content);
            const paragraphs = cleanedContent.replace(/\r\n/g, "\n").split("\n");
            const margin = 50;
            const width = 600;
            const contentWidth = width - margin * 2; // 500
            const bodyFontSize = 11;
            const bodyLineHeight = 16;

            let isFirstContentPage = true;
            let currentPage = pdfDoc.addPage([600, 800]);
            drawCopyrightFooter(currentPage, author, timesRoman);

            let currentY = 730;
            let pageHasContent = false;

            const getPageLimitY = () => (isFirstContentPage ? 105 : 50);

            const startNewContentPage = () => {
                currentPage = pdfDoc.addPage([600, 800]);
                currentY = 730;
                isFirstContentPage = false;
                pageHasContent = false;
            };

            for (let i = 0; i < paragraphs.length; i++) {
                let p = paragraphs[i].trim();
                if (!p) {
                    currentY -= 10;
                    if (currentY < getPageLimitY()) {
                        startNewContentPage();
                    }
                    continue;
                }

                const isChapterLabel = (line) => {
                    return line.startsWith("Chapter") || line.startsWith("CHAPTER");
                };

                if (isChapterLabel(p)) {
                    if (pageHasContent || !isFirstContentPage) {
                        startNewContentPage();
                    }

                    currentY = 680;
                    const headingFontSize = 18;
                    const textWidth = timesRomanBold.widthOfTextAtSize(p, headingFontSize);
                    currentPage.drawText(p, {
                        x: (600 - textWidth) / 2,
                        y: currentY,
                        size: headingFontSize,
                        font: timesRomanBold,
                        color: rgb(0.15, 0.15, 0.15),
                    });

                    currentY -= 28;
                    pageHasContent = true;

                    if (i + 1 < paragraphs.length) {
                        const nextLine = paragraphs[i + 1].trim();
                        if (nextLine && nextLine.length < 60 && !nextLine.endsWith(".") && (nextLine === nextLine.toUpperCase() || nextLine.split(" ").every(w => w[0] === w[0]?.toUpperCase() || w.length <= 3))) {
                            const subFontSize = 13;
                            const subTextWidth = timesItalic.widthOfTextAtSize(nextLine, subFontSize);
                            currentPage.drawText(nextLine, {
                                x: (600 - subTextWidth) / 2,
                                y: currentY,
                                size: subFontSize,
                                font: timesItalic,
                                color: rgb(0.3, 0.3, 0.3),
                            });
                            currentY -= 24;
                            i++;
                        }
                    }

                    currentY -= 16;
                    continue;
                }

                const indentSize = 0;
                const isFirstParagraphOfChapter = (currentY > 600);

                const words = p.split(/\s+/);
                let currentLine = "";
                let isFirstLineOfParagraph = true;

                for (const word of words) {
                    const testLine = currentLine ? `${currentLine} ${word}` : word;
                    const currentLineIndent = (isFirstLineOfParagraph && !isFirstParagraphOfChapter) ? indentSize : 0;
                    const testWidth = timesRoman.widthOfTextAtSize(testLine, bodyFontSize);

                    if (testWidth > (contentWidth - currentLineIndent)) {
                        currentPage.drawText(currentLine, {
                            x: margin + currentLineIndent,
                            y: currentY,
                            size: bodyFontSize,
                            font: timesRoman,
                            color: rgb(0.12, 0.12, 0.12),
                        });
                        currentY -= bodyLineHeight;
                        pageHasContent = true;

                        if (currentY < getPageLimitY()) {
                            startNewContentPage();
                        }
                        currentLine = word;
                        isFirstLineOfParagraph = false;
                    } else {
                        currentLine = testLine;
                    }
                }

                if (currentLine) {
                    const currentLineIndent = (isFirstLineOfParagraph && !isFirstParagraphOfChapter) ? indentSize : 0;
                    currentPage.drawText(currentLine, {
                        x: margin + currentLineIndent,
                        y: currentY,
                        size: bodyFontSize,
                        font: timesRoman,
                        color: rgb(0.12, 0.12, 0.12),
                    });
                    currentY -= bodyLineHeight;
                    pageHasContent = true;

                    if (currentY < getPageLimitY()) {
                        startNewContentPage();
                    }
                }

                currentY -= 6;
                if (currentY < getPageLimitY()) {
                    startNewContentPage();
                }
            }
        } else {
            const emptyPage = pdfDoc.addPage([600, 800]);
            emptyPage.drawText("This book contains no text content.", {
                x: 50,
                y: 700,
                size: 14,
                font: timesRoman,
                color: rgb(0.5, 0.5, 0.5),
            });
            drawCopyrightFooter(emptyPage, author, timesRoman);
        }
    }

    // 4. Draw Back Cover
    const backCoverPage = pdfDoc.addPage([coverWidth, coverHeight]);
    let drawBackImageSuccess = false;
    
    try {
        if (coverMode === "image" && backCover) {
            try {
                const imgBytes = await fetchImageBytesWithFallback(backCover);
                let img;
                try {
                    img = await pdfDoc.embedJpg(imgBytes);
                } catch {
                    img = await pdfDoc.embedPng(imgBytes);
                }
                
                backCoverPage.drawImage(img, {
                    x: 0,
                    y: 0,
                    width: coverWidth,
                    height: coverHeight,
                });
                drawBackImageSuccess = true;
            } catch (error) {
                console.error("Error embedding back cover image, falling back to solid color:", error);
            }
        }
        
        if (!drawBackImageSuccess) {
            backCoverPage.drawRectangle({
                x: 0,
                y: 0,
                width: coverWidth,
                height: coverHeight,
                color: hexToRgbColor(coverBgColor, rgb(0.86, 0.15, 0.15)),
            });
        }

        // Draw Back Cover overlays (Blurb panel, Profile panel & Barcode)
        // Description Panel
        const descX = (descLeft / 100) * coverWidth;
        const descTopPx = (descTop / 100) * coverHeight;
        const finalDescWidth = (descWidth / 100) * coverWidth;
        const maxDescWidth = finalDescWidth - 24; // 12pt padding left/right
        
        // Fetch and embed signature if available
        let sigImg = null;
        let sigWidth = 0;
        let sigHeight = 30;
        if (authorSignature) {
            try {
                const sigBytes = await fetchImageBytesWithFallback(authorSignature);
                try {
                    sigImg = await pdfDoc.embedJpg(sigBytes);
                } catch {
                    sigImg = await pdfDoc.embedPng(sigBytes);
                }
                if (sigImg) {
                    sigWidth = Math.min(100, (sigImg.width / sigImg.height) * sigHeight);
                }
            } catch (err) {
                console.warn("Could not embed author signature on PDF back cover:", err);
            }
        }

        const descLayout = layoutRichTextBlock(
            activeCoverDescription,
            {
                maxWidth: maxDescWidth,
                defaultSize: descSize,
                casing: descCase,
                blockFontWeight: descFontWeight,
                blockFontStyle: descFontStyle,
                activeFonts
            }
        );

        const descRectHeight = descLayout.totalHeight + 20 + (sigImg ? 38 : 0);
        const descRectY = coverHeight - descTopPx - descRectHeight;

        backCoverPage.drawRectangle({
            x: descX,
            y: descRectY,
            width: finalDescWidth,
            height: descRectHeight,
            color: rgb(0.05, 0.05, 0.05),
            opacity: coverMode === "solid" ? 0.25 : 0.65,
        });

        const descRgbColor = hexToRgbColor(descColor, rgb(1, 1, 1));
        
        // Draw Left Border
        backCoverPage.drawRectangle({
            x: descX,
            y: descRectY,
            width: 3,
            height: descRectHeight,
            color: descRgbColor,
        });

        const textY = descRectY + descRectHeight - 10 - descSize;
        drawLayoutLines(
            backCoverPage,
            descLayout,
            {
                x: descX + 12,
                yStart: textY,
                maxWidth: maxDescWidth,
                defaultColor: descRgbColor,
                alignment: descAlign
            }
        );

        // Draw author signature just below text if available
        if (sigImg) {
            let sigX = descX + finalDescWidth - sigWidth - 12;
            if (descAlign === "left") {
                sigX = descX + 12;
            } else if (descAlign === "center") {
                sigX = descX + (finalDescWidth - sigWidth) / 2;
            }
            backCoverPage.drawImage(sigImg, {
                x: sigX,
                y: descRectY + 10,
                width: sigWidth,
                height: sigHeight,
            });
        }

        // Fetch and draw Author portrait photo
        const authorPortraitUrl = authorPhoto || `https://image.pollinations.ai/prompt/professional%20headshot%20portrait%20of%20author%20named%20${encodeURIComponent(author)}%20wearing%20smart%20casual%20attire%20studio%20lighting%20plain%20background?width=150&height=150&nologo=true&seed=42`;
        let authorImg = null;
        try {
            const authorImgBytes = await fetchImageBytesWithFallback(authorPortraitUrl);
            try {
                authorImg = await pdfDoc.embedJpg(authorImgBytes);
            } catch {
                authorImg = await pdfDoc.embedPng(authorImgBytes);
            }
        } catch (err) {
            console.warn("Could not embed author portrait photo on PDF back cover, showing placeholder:", err);
        }

        // Profile Panel
        const profileX = (profileLeft / 100) * coverWidth;
        const profileTopPx = (profileTop / 100) * coverHeight;
        const finalProfileWidth = (profileWidth / 100) * coverWidth;
        const maxBioWidth = finalProfileWidth - 80 - 30; // 80 pt photo, 30 pt padding

        const profAuthorLayout = layoutRichTextBlock(
            activeCoverAuthor,
            {
                maxWidth: maxBioWidth,
                defaultSize: profileSize + 2,
                casing: authorCase,
                blockFontWeight: authorFontWeight,
                blockFontStyle: authorFontStyle,
                activeFonts
            }
        );

        const profBioLayout = layoutRichTextBlock(
            activeCoverProfileText,
            {
                maxWidth: maxBioWidth,
                defaultSize: profileSize,
                casing: profileCase,
                blockFontWeight: profileFontWeight,
                blockFontStyle: profileFontStyle,
                activeFonts
            }
        );

        const profileTextHeight = profAuthorLayout.totalHeight + profBioLayout.totalHeight + 12 + 14;
        const profileRectHeight = Math.max(100, profileTextHeight + 20);
        const profileRectY = coverHeight - profileTopPx - profileRectHeight;

        backCoverPage.drawRectangle({
            x: profileX,
            y: profileRectY,
            width: finalProfileWidth,
            height: profileRectHeight,
            color: rgb(0.05, 0.05, 0.05),
            opacity: coverMode === "solid" ? 0.25 : 0.75,
        });

        const profileRgbColor = hexToRgbColor(profileColor, rgb(1, 1, 1));

        // Draw profile photo (80x80)
        const photoY = profileRectY + (profileRectHeight - 80) / 2;
        if (authorImg) {
            backCoverPage.drawImage(authorImg, {
                x: profileX + 10,
                y: photoY,
                width: 80,
                height: 80,
            });
        } else {
            backCoverPage.drawRectangle({
                x: profileX + 10,
                y: photoY,
                width: 80,
                height: 80,
                color: rgb(0.3, 0.3, 0.3),
            });
            const initials = activeCoverAuthor.split(/\s+/).map(w => w[0]).join("").toUpperCase().substring(0, 2);
            const initialsWidth = activeFonts.bold.widthOfTextAtSize(initials, 18);
            backCoverPage.drawText(initials, {
                x: profileX + 10 + (80 - initialsWidth) / 2,
                y: photoY + 30,
                size: 18,
                font: activeFonts.bold,
                color: rgb(0.7, 0.7, 0.7),
            });
        }

        // Draw profile text details
        const detailX = profileX + 100;
        const bioY = profileRectY + profileRectHeight - 15 - (profileSize + 2); // start under title

        const profileTitle = "ABOUT THE BOOK";
        const profileAuthorName = applyTextCase(activeCoverAuthor, authorCase);

        // Align ABOUT THE BOOK title
        const ptWidth = activeFonts.bold.widthOfTextAtSize(profileTitle, profileSize - 1);
        let ptX = detailX;
        if (profileAlign === "center") ptX = detailX + (maxBioWidth - ptWidth) / 2;
        else if (profileAlign === "right") ptX = detailX + (maxBioWidth - ptWidth);

        backCoverPage.drawText(profileTitle, {
            x: ptX,
            y: bioY + 12,
            size: profileSize - 1,
            font: activeFonts.bold,
            color: profileRgbColor,
        });

        // Draw Author Name and Bio dynamically
        const authorEndY = drawLayoutLines(
            backCoverPage,
            profAuthorLayout,
            {
                x: detailX,
                yStart: bioY,
                maxWidth: maxBioWidth,
                defaultColor: profileRgbColor,
                alignment: profileAlign
            }
        );

        drawLayoutLines(
            backCoverPage,
            profBioLayout,
            {
                x: detailX,
                yStart: authorEndY - 4,
                maxWidth: maxBioWidth,
                defaultColor: profileRgbColor,
                alignment: profileAlign
            }
        );

        // Draw mock ISBN barcode
        const barcodeX = coverWidth - 130;
        const barcodeY = 60;
        drawMockBarcode(backCoverPage, barcodeX, barcodeY, 100, 55);

    } catch (error) {
        console.error("Error drawing back cover:", error);
        // Fallback: draw basic cover style if drawing the advanced layout failed
        backCoverPage.drawRectangle({
            x: 0,
            y: 0,
            width: coverWidth,
            height: coverHeight,
            color: hexToRgbColor(coverBgColor, rgb(0.1, 0.1, 0.2)),
        });
        backCoverPage.drawText(title, {
            x: 50,
            y: coverHeight / 2,
            size: 24,
            font: activeFonts.bold,
            color: hexToRgbColor(coverTextColor, rgb(1, 0.84, 0)),
            maxWidth: coverWidth - 100,
        });
    }

    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
};